import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/show-toast';
import { useCreateInvitation } from '@/services/invitations/queries';
import type { Invitation, AllUserRoles } from '@/services/types';
import { usePostHog } from 'posthog-js/react';

const invitationSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  role: z.string().min(1, 'Role is required')
});

type InvitationFormData = z.infer<typeof invitationSchema>;

interface InvitationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: 'organisation' | 'workspace';
  organisationId?: string;
  workspaceId?: string;
  onSuccess?: () => void;
}

const getAvailableRoles = (context: 'organisation' | 'workspace'): AllUserRoles[] => {
  if (context === 'organisation') {
    return ['org_admin', 'org_user'];
  } else {
    return ['workspace_admin', 'workspace_user'];
  }
};

const formatRoleLabel = (role: AllUserRoles): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export function InvitationModal({ 
  open, 
  onOpenChange, 
  context,
  organisationId,
  workspaceId,
  onSuccess
}: InvitationModalProps) {
  const posthog = usePostHog();

  const form = useForm<InvitationFormData>({
    resolver: zodResolver(invitationSchema),
    defaultValues: {
      email: '',
      role: ''
    }
  });

  const availableRoles = getAvailableRoles(context);

  const { mutate: createInvitation, isPending: isSubmitting } = useCreateInvitation(
    () => {
      posthog?.capture('form_submitted', { form_name: 'send_invitation', success: true, context });
      posthog?.capture('modal_closed', { modal_name: 'invitation_modal', completed: true });
      showToast('success', 'Invitation sent successfully');
      form.reset();
      onOpenChange(false);
      onSuccess?.();
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'send_invitation', success: false, context });
      showToast('error', error?.response?.data?.message || 'Failed to send invitation');
    }
  );

  const handleSubmit = async (data: InvitationFormData) => {
    const invitationData: Invitation = {
      email: data.email,
      role: data.role as AllUserRoles,
      context,
      ...(context === 'organisation' && organisationId && { organisationId }),
      ...(context === 'workspace' && workspaceId && { workspaceId })
    };

    createInvitation(invitationData);
  };

  const handleClose = () => {
    posthog?.capture('modal_closed', { modal_name: 'invitation_modal', completed: false });
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Send Invitation</DialogTitle>
          <DialogDescription>
            Invite a new user to join your {context === 'organisation' ? 'organisation' : 'workspace'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder='user@example.com'
                      type='email'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='role'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder='Select a role' />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {formatRoleLabel(role)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                disabled={isSubmitting}
                isLoading={isSubmitting}
                trackingEvent='invitation_sent'
                trackingProperties={{ context }}
              >
                Send Invitation
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}